// Settings → MCP / Integrations.
//
// Read-only surface that shows, for the ACTIVE project + branch, the MCP server
// endpoints you can connect an external client (Claude Code, etc.) to. There is
// no host config here — the cortex MCP is served on the SAME public origin as
// this UI (nginx routes /mcp, /assistant, /events, /ws, /.well-known to the
// assistant container), so the endpoints are just window.location.origin + the
// per-harness path. The paired Flow project's MCP URL comes from the project row
// (flow_mcp_url via /api/projects).
//
// Harness paths (assistant/server.js route table):
//   governance      full read+write   /mcp/p/<slug>/b/<branch>
//   flow-assistant   read-only        /mcp/ro/p/<slug>
//   analyst          analytics        /mcp/analytics  → LOOPBACK-ONLY (in-product
//                                      sub-agent dials it; NOT externally connectable)
//   paired Flow      engine           <flow_mcp_url>  (separate Flow host)
import React, { useState, useEffect, useMemo } from "react";
import { Plug, Copy, Check, ShieldCheck, Eye, Workflow, BarChart3, Info, KeyRound, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useProject } from "@/contexts/ProjectContext";
import { listProjects } from "@/services/projects";
import { listMcpTokens, mintMcpToken, revokeMcpToken } from "../services/integrations";

// ACCESS TOKENS for external agent frameworks. An interactive MCP client
// completes the OAuth consent in a browser; a headless one (a LangGraph graph,
// a Bedrock AgentCore gateway target, a CI job) cannot — so a signed-in person
// mints the same bearer here, labelled, and revokes it when the integration
// goes. The token is shown ONCE; the list carries its prefix only.
function AccessTokensCard({ slug, branch, origin }) {
  const [tokens, setTokens] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState(null); // { token, prefix, label }
  const [err, setErr] = useState(null);
  const reload = () => listMcpTokens().then((r) => setTokens(r.tokens || [])).catch((e) => { setTokens([]); setErr(e?.message || String(e)); });
  useEffect(() => { reload(); }, []);
  const mint = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await mintMcpToken(label.trim());
      setMinted(r); setLabel("");
      await reload();
    } catch (e) { setErr(e?.message || String(e)); } finally { setBusy(false); }
  };
  const revoke = async (prefix) => {
    setErr(null);
    try { await revokeMcpToken(prefix); if (minted?.prefix === prefix) setMinted(null); await reload(); }
    catch (e) { setErr(e?.message || String(e)); }
  };
  const mcpUrl = slug ? `${origin}/mcp/p/${encodeURIComponent(slug)}/b/${encodeURIComponent(branch)}` : `${origin}/mcp/p/<project>/b/main`;
  const orchUrl = slug ? `${origin}/mcp/orch/p/${encodeURIComponent(slug)}/b/${encodeURIComponent(branch)}` : `${origin}/mcp/orch/p/<project>/b/main`;
  const tok = minted?.token || "<token>";
  const snippets = [
    { title: "LangGraph / LangChain (langchain-mcp-adapters)", code:
`from langchain_mcp_adapters.client import MultiServerMCPClient
client = MultiServerMCPClient({
    "cortex": {
        "transport": "streamable_http",
        "url": "${mcpUrl}",
        "headers": {"Authorization": "Bearer ${tok}"},
    }
})
tools = await client.get_tools()   # every fork endpoint of the project, typed` },
    { title: "Amazon Bedrock AgentCore Gateway (MCP target)", code:
`# Gateway target type: MCP server, Streamable HTTP
# URL:            ${mcpUrl}
# Outbound auth:  API key → header "Authorization", value "Bearer ${tok}"
# The project's tools appear as gateway tools; the agents themselves are tools on
#   ${orchUrl}  (delegate_to_<agent>)` },
    { title: "Claude Code / any MCP client", code:
`claude mcp add --transport http cortex-${slug || "project"} ${mcpUrl} \\
  --header "Authorization: Bearer ${tok}"` },
  ];
  return (
    <div className="bg-card-bg rounded-[8px] p-4 md:p-5 flex flex-col gap-3" data-testid="settings-access-tokens">
      <div className="flex items-center gap-2">
        <KeyRound size={18} color="#377AD6" />
        <h2 className="text-base font-medium" style={{ color: "var(--page-text)" }}>Access tokens for external MCP clients</h2>
      </div>
      <p className="text-sm text-page-muted">
        A headless MCP client (an agent runtime, a gateway, a CI job) has no browser for the OAuth consent. Mint a bearer here instead:
        it carries your identity and exactly your access, works on every MCP endpoint above, never expires, and stops the moment you revoke it.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="What is this token for? e.g. allvue-langgraph-dev"
          data-testid="mcp-token-label" maxLength={120}
          className="flex-1 min-w-[16rem] h-8 px-2.5 rounded-[5px] border border-page-divider bg-transparent text-sm text-page-text" />
        <button type="button" onClick={mint} disabled={busy || !label.trim()} data-testid="mcp-token-mint"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-page-divider text-sm cursor-pointer bg-card-bg hover:border-nav-active disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />} Create token
        </button>
      </div>
      {err ? <div className="text-sm text-red-600" data-testid="mcp-token-error">{err}</div> : null}
      {minted ? (
        <div className="rounded-[6px] border border-emerald-500/40 bg-emerald-500/10 p-3 grid gap-2" data-testid="mcp-token-minted">
          <div className="text-sm text-page-text">Token <b>{minted.label}</b> — copy it now; it is not shown again.</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs break-all px-2 py-1.5 rounded bg-page-bg text-page-text" data-testid="mcp-token-value">{minted.token}</code>
            <CopyButton value={minted.token} />
          </div>
        </div>
      ) : null}
      <div className="grid gap-1" data-testid="mcp-token-list">
        {tokens === null ? <div className="text-sm text-page-muted">Loading…</div>
          : tokens.length === 0 ? <div className="text-sm text-page-muted">No tokens yet.</div>
          : tokens.map((t) => (
            <div key={t.prefix} className="flex flex-wrap items-center gap-3 text-sm border-t border-page-divider pt-2" data-testid="mcp-token-row" data-prefix={t.prefix}>
              <span className="font-mono text-xs text-page-muted">{t.prefix}…</span>
              <span className="text-page-text">{t.label || "(no label)"}</span>
              <span className="text-page-muted text-xs">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</span>
              <button type="button" onClick={() => revoke(t.prefix)} data-testid="mcp-token-revoke"
                className="ml-auto inline-flex items-center gap-1 h-7 px-2 rounded-[5px] border border-page-divider text-xs cursor-pointer bg-transparent hover:border-red-500 hover:text-red-600">
                <Trash2 size={12} /> Revoke
              </button>
            </div>
          ))}
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer text-page-muted">How to connect a client</summary>
        <div className="grid gap-3 pt-2">
          {snippets.map((sn) => (
            <div key={sn.title} className="grid gap-1">
              <div className="text-xs font-medium text-page-text">{sn.title}</div>
              <pre className="m-0 font-mono text-[11px] whitespace-pre-wrap break-all px-2.5 py-2 rounded bg-page-bg text-page-text overflow-auto">{sn.code}</pre>
            </div>
          ))}
          <div className="text-xs text-page-muted flex items-start gap-1.5"><Info size={12} className="mt-0.5 shrink-0" />
            Transport is MCP Streamable HTTP. The project endpoint exposes the project's typed tools; the orchestrator endpoint exposes the project's AGENTS as tools (<span className="font-mono">delegate_to_&lt;agent&gt;</span>), each answering under its own guardrails.
          </div>
        </div>
      </details>
    </div>
  );
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  return (
    <button onClick={onCopy} disabled={!value}
      className="flex items-center gap-1.5 h-8 px-2.5 rounded-[5px] border border-page-divider text-xs cursor-pointer bg-card-bg hover:border-nav-active transition-colors disabled:opacity-50 shrink-0">
      {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
    </button>
  );
}

function EndpointCard({ icon: Icon, color, title, persona, mode, url, connectable, note }) {
  // Suggested Claude Code add command (external clients only).
  const addCmd = connectable && url
    ? `claude mcp add --transport http ${persona} ${url}`
    : null;
  return (
    <div className="bg-card-bg rounded-[8px] p-4 md:p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-[6px] flex items-center justify-center shrink-0" style={{ background: color + "20", color }}>
            <Icon size={17} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-medium" style={{ color: "var(--page-text)" }}>{title}</h3>
            <p className="text-xs text-page-muted">{persona}</p>
          </div>
        </div>
        <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap shrink-0"
          style={{ background: color + "20", color }}>{mode}</span>
      </div>

      {connectable ? (
        <>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 text-xs font-mono bg-page-bg rounded-[5px] px-2.5 py-2 overflow-x-auto whitespace-nowrap">{url}</code>
            <CopyButton value={url} />
          </div>
          {addCmd && (
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 text-xs font-mono bg-page-bg rounded-[5px] px-2.5 py-2 overflow-x-auto whitespace-nowrap text-page-muted">{addCmd}</code>
              <CopyButton value={addCmd} />
            </div>
          )}
        </>
      ) : (
        <div className="text-xs text-page-muted flex items-start gap-1.5">
          <Info size={13} className="mt-0.5 shrink-0" /> <span>{note}</span>
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const { projectSlug: slug, branch } = useProject();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await listProjects();
        if (alive) setProjects(p || []);
      } catch {
        if (alive) setProjects([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const activeProject = useMemo(
    () => projects.find((p) => p.slug === slug) || null,
    [projects, slug]
  );

  // The assistant MCP shares this UI's public origin (nginx-routed). Build the
  // cortex endpoints from it; the Flow endpoint comes off the project row.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const enc = (s) => encodeURIComponent(s);

  const endpoints = slug ? [
    {
      icon: ShieldCheck, color: "#377AD6",
      title: "Governance", persona: "cortex-" + slug,
      mode: "read + write", connectable: true,
      url: `${origin}/mcp/p/${enc(slug)}/b/${enc(branch)}`,
    },
    {
      icon: Eye, color: "#0EA5E9",
      title: "Flow-assistant", persona: "cortex-" + slug + "-ro",
      mode: "read-only", connectable: true,
      url: `${origin}/mcp/ro/p/${enc(slug)}`,
    },
    {
      icon: Workflow, color: "#7c3aed",
      title: "Paired Flow project", persona: "flow-" + slug,
      mode: "engine", connectable: !!activeProject?.flow_mcp_url,
      url: activeProject?.flow_mcp_url || null,
      note: "No paired Flow MCP URL recorded for this project yet (set once the project's Flow fork is provisioned).",
    },
    {
      icon: BarChart3, color: "#16a34a",
      title: "Analyst (analytics)", persona: "analyst",
      mode: "in-product only", connectable: false,
      note: "The analyst analytics MCP is loopback-only — the in-product Data Analyst chat dials it internally. It is not exposed for external clients.",
    },
  ] : [];

  const shell = "pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5";

  return (
    <div className={shell}>
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[32px] font-medium leading-tight text-page-title">Settings</h1>
        <p className="text-sm font-normal text-page-muted">Connection details and integrations.</p>
      </div>

      <div className="bg-card-bg rounded-[8px] p-4 md:p-5 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Plug size={18} color="#377AD6" />
          <h2 className="text-base font-medium" style={{ color: "var(--page-text)" }}>MCP / Integrations</h2>
        </div>
        <p className="text-sm text-page-muted">
          Connect an MCP client (e.g. Claude Code) to drive this project's tools. Endpoints are scoped to the
          active project{slug ? <> — <span className="font-mono">{slug}</span> on branch <span className="font-mono">{branch}</span></> : ""}.
          Authenticate with <span className="font-mono">/mcp</span> after adding (OAuth in the browser).
        </p>
      </div>

      {loading ? (
        <div className="text-page-muted text-sm">Loading…</div>
      ) : !slug ? (
        <div className="bg-card-bg rounded-[8px] p-5 text-page-muted text-sm">
          No active project. Pick one on the Projects page to see its MCP endpoints.
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(320px,1fr))]">
          {endpoints.map((e) => <EndpointCard key={e.title} {...e} />)}
        </div>
      )}

      <AccessTokensCard slug={slug} branch={branch} origin={origin} />
    </div>
  );
}
